/**
 * Lendmarq color system — the single source of color for the vendored surfaces.
 * The reasoning behind each choice lives in ./lendmarq-color-system.md; this
 * file is the implementation. Do NOT use MUI's grey[100]…grey[900] or invent a
 * new green/amber/red per screen — import from here.
 *
 * The rule that explains most of the file: unlike MARQ, **brand = action**.
 * MARQ's #56A0D3 fails WCAG AA under white text, so tpo splits it into a brand
 * accent (blue[400]) and a darker action color (blue[600]). Lendmarq's
 * #1F5AF2 measures 5.5:1 on white, so one blue does both jobs and it sits at
 * **600** — buttons, links, indicators and the logo are all the same color.
 *
 * Picking a step: tints 50–100, borders-on-tint 200, focus ring 300,
 * decorative 400, header surface 500, interactive 600, hover 700, pressed 800.
 *
 * LENDMARQ DIVERGENCE from tpo-portal-frontend, which ramps off MARQ's #56A0D3
 * at ~204° with the brand at 400. See CLAUDE.md divergence #2 — #1F5AF2 is the
 * same brand blue the Eastview recolor table maps every teal onto.
 */

// Blue ramp — hue held at ~222°, derived from the Lendmarq brand #1F5AF2.
// 100 / 500 / 600 were sampled from the live product (active-state tint, app
// header, logo + selected state); the rest of the ramp is built around them.
export const blue = {
  50: "#F4F7FF", // page-level tinted surfaces, hover on white rows
  100: "#E8EEFE", // sampled — active-state tint (selected chips, rows, steps)
  200: "#C7D7FC", // borders on tinted surfaces, softer indicator on the header
  300: "#9AB6FA", // focus rings
  400: "#6B93F8", // decorative accents, secondary chart shades
  500: "#346BFB", // sampled — app header surface (4.5:1 under white text)
  600: "#1F5AF2", // sampled — BRAND & primary action (5.5:1 on white)
  700: "#1A4BD2", // primary hover, tinted-surface text (7:1 on white)
  800: "#153C9F", // primary pressed
};

// Cool-gray neutrals (Tailwind slate), same temperature family as the blue.
// Shared verbatim with MARQ — only the blues differ between the two products,
// which is what makes cross-product work painless.
export const slate = {
  50: "#F8FAFC",
  100: "#F1F5F9",
  200: "#E2E8F0",
  300: "#CBD5E1",
  400: "#94A3B8",
  500: "#64748B",
  600: "#475569",
  700: "#334155",
  800: "#1E293B",
  900: "#0F172A",
};

// Anchors the system: headings, primary body text (16.9:1).
export const ink = slate[900];

/**
 * Second accent hue, for places that genuinely need one (chart series, a second
 * data dimension). Never for buttons — secondary buttons are neutral.
 *
 * Violet is the default: Lendmarq's royal blue is vivid, and a violet neighbour
 * harmonizes with it better than teal, which reads slightly off-palette beside
 * #1F5AF2.
 */
export const violet = { main: "#7C5CFA", tint: "#F3F0FF", text: "#6D4AEF" };

/**
 * The maximum-separation alternative to `violet` — for a chart where two series
 * must never be confused with each other or with the brand blue. Prefer
 * `violet` everywhere else.
 */
export const teal = { main: "#0D9488", tint: "#F0FDFA" };

/**
 * Corner radius for anything that sits in a form row and reads as a field.
 *
 * MUI inputs get this from the theme's MuiOutlinedInput override, but the
 * hand-rolled controls (the address field's summary row, and anything else that
 * draws its own box beside a real input) have no way to inherit it — so they
 * import this instead of picking a number. A field that is 2px off its
 * neighbour is exactly the kind of drift nobody files a bug about and everyone
 * notices. Change it here and in generateMuiThemeDefaults together.
 */
export const INPUT_BORDER_RADIUS = "10px";

/**
 * The signature selected state: a **tinted outline chip** — blue-100 fill, 1px
 * blue-600 border, blue-600 text at weight 600. Unselected siblings are white
 * with a slate-300 border. #1F5AF2 on #E8EEFE is 4.6:1, so the pattern is
 * AA-safe as-is; in dense/small-text contexts swap the text to `hoverText`
 * (blue-700, 6:1) while leaving the border on 600.
 *
 * Use it everywhere a user picks one-of-N, so the product has ONE selected
 * look: choice chips, the stepper's active step, segmented filters and view
 * switchers, selected list/table rows. Full-width rows and steps skip the
 * border and use the fill alone.
 *
 * Tabs are the deliberate exception — they stay the underline pattern
 * (blue-600 text + 2px blue-600 indicator), because tabs are navigation and
 * chips are selection, and keeping the two looks distinct helps users tell
 * which is which.
 */
export const selection = {
  restBg: "#FFFFFF",
  restBorder: slate[300],
  restText: slate[700],
  hoverBg: slate[50],
  hoverBorder: slate[400],

  bg: blue[100],
  border: blue[600],
  text: blue[600],
  // Selected + hover deepens the border and text, not the fill.
  selectedHoverBorder: blue[700],
  selectedHoverText: blue[700],

  focusRing: `0 0 0 3px ${blue[300]}`,
};

/**
 * The blue app header — a *saturated colored* surface, not a dark one, which
 * changes one thing versus MARQ's navy header: the brand-blue underline trick
 * doesn't work, because blue on blue disappears. White takes over both roles.
 *
 * Never put a blue-600 element directly on the blue-500 header; a filled
 * primary button there has too little contrast against its own background, so
 * it becomes white-outlined instead (`buttonBorder`).
 */
export const onHeader = {
  bg: blue[500],
  fg: "#FFFFFF",
  // Inactive nav items / icon buttons. Don't go lighter than this — white on
  // blue-500 is 4.5:1, right at the AA line, so header text also stays at
  // 13px+ / weight 500.
  fgMuted: "rgba(255,255,255,.78)",
  hoverBg: "rgba(255,255,255,.12)",
  buttonBorder: "rgba(255,255,255,.6)",
  indicator: "#FFFFFF", // active underline — white, not blue
  indicatorSoft: blue[200], // softer alternative
  focusRing: blue[200],
};

/**
 * Status tint pairs — light background + darker text of the same hue. Pin these
 * exact pairs so every pill in the product matches; `solid` is for filled
 * alerts and destructive buttons.
 *
 * Shared with MARQ except `info`, which inherits Lendmarq's blue: the pattern
 * is shared, the blue is per-brand.
 */
export const status = {
  success: { bg: "#ECFDF5", fg: "#047857", solid: "#15803D" }, // completed / submitted
  info: { bg: blue[100], fg: blue[700], solid: blue[600] }, // in progress
  warning: { bg: "#FFFBEB", fg: "#B45309", solid: "#B45309" }, // revisions needed
  neutral: { bg: slate[100], fg: slate[500], solid: slate[500] }, // not started
  error: { bg: "#FEF2F2", fg: "#B91C1C", solid: "#B91C1C" }, // declined / error
};

const colors = {
  // Flat slate aliases — the shape the app already imports.
  slate900: slate[900],
  slate800: slate[800],
  slate700: slate[700],
  slate600: slate[600],
  slate500: slate[500],
  slate400: slate[400],
  slate300: slate[300],
  slate200: slate[200],
  slate100: slate[100],
  slate50: slate[50],

  white: "#FFFFFF",
  black: "#000000",
  ink,

  // Blue ramp, flat.
  blue50: blue[50],
  blue100: blue[100],
  blue200: blue[200],
  blue300: blue[300],
  blue400: blue[400],
  blue500: blue[500],
  blue600: blue[600],
  blue700: blue[700],
  blue800: blue[800],

  // Role aliases — prefer these at call sites; they say what the color is FOR.
  // `brand` and `action` are deliberately the same value: see the header note.
  brand: blue[600],
  accent: blue[400], // decorative color next to text
  action: blue[600], // color under white text
  actionHover: blue[700],
  actionPressed: blue[800],
  focusRing: blue[300],
  tint: blue[100], // active-state / selected fill
  tintFg: blue[700], // text on a blue tint when extra contrast is wanted

  // Header surface (see `onHeader` for the full on-header set).
  headerBg: onHeader.bg,
  headerFg: onHeader.fg,
  headerFgMuted: onHeader.fgMuted,

  // Second accent. Violet by default; teal only for maximum separation.
  violet: violet.main,
  violetTint: violet.tint,
  violetFg: violet.text,
  teal: teal.main,
  tealTint: teal.tint,

  // Semantic solids + tint backgrounds. `*Fg` is the tint pair's text color.
  success: status.success.solid,
  successBg: status.success.bg,
  successFg: status.success.fg,
  warning: status.warning.solid,
  warningBg: status.warning.bg,
  warningFg: status.warning.fg,
  danger: status.error.solid,
  dangerBg: status.error.bg,
  dangerFg: status.error.fg,
  info: status.info.solid,
  infoBg: status.info.bg,
  infoFg: status.info.fg,
  neutralBg: status.neutral.bg,
  neutralFg: status.neutral.fg,
};

export default colors;
